import React, { useState } from 'react';
import { Button, ClearableInput, Spinner } from '@rio-cloud/rio-uikit';
import { NotificationUserFormData } from '../../../../../models/notificationUser';
import { fetchUser } from '../../../../../api/fetchUser';

interface NotificationUserFormProps {
  addNotificationUser: (data: NotificationUserFormData) => Promise<boolean>;
  onSuccess: () => void;
  onCancel: () => void;
}

const NotificationUserForm: React.FC<NotificationUserFormProps> = ({
  addNotificationUser,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<NotificationUserFormData>({
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
  });

  const handleUserIdChange = async (value: string) => {
    setFormData(prev => ({
      ...prev,
      userId: value,
    }));

    if (errors.userId) {
      setErrors(prev => ({
        ...prev,
        userId: '',
      }));
    }

    // Se o userId foi preenchido, buscar o usuário
    if (value.trim()) {
      setLoadingUser(true);
      try {
        const user = await fetchUser(value.trim());
        if (user) {
          setFormData(prev => ({
            ...prev,
            userId: value.trim(),
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            userId: 'Usuário não encontrado',
          }));
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setErrors(prev => ({
          ...prev,
          userId: 'Erro ao buscar usuário',
        }));
      } finally {
        setLoadingUser(false);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userId.trim()) {
      newErrors.userId = 'ID do usuário é obrigatório';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Nome é obrigatório';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Sobrenome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const success = await addNotificationUser(formData);

      if (success) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof NotificationUserFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-size-h3">Dados do Usuário</div>
        <div>
          <div>
            <label htmlFor="userId" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
              ID do Usuário *
            </label>
            <div className="relative">
              <ClearableInput
                id="userId"
                value={formData.userId}
                onChange={handleUserIdChange}
                placeholder="Digite o ID do usuário"
                disabled={loadingUser}
              />
              {loadingUser && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Spinner />
                </div>
              )}
            </div>
            {errors.userId && (
              <p className="text-red-600 text-sm mt-1">{errors.userId}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="firstName" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            Nome *
          </label>
          <ClearableInput
            id="firstName"
            value={formData.firstName}
            onChange={(value) => handleInputChange('firstName', value)}
            placeholder="Nome será preenchido automaticamente"
            disabled={true}
          />
          {errors.firstName && (
            <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            Sobrenome *
          </label>
          <ClearableInput
            id="lastName"
            value={formData.lastName}
            onChange={(value) => handleInputChange('lastName', value)}
            placeholder="Sobrenome será preenchido automaticamente"
            disabled={true}
          />
          {errors.lastName && (
            <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            Email *
          </label>
          <ClearableInput
            id="email"
            value={formData.email}
            onChange={(value) => handleInputChange('email', value)}
            placeholder="Email será preenchido automaticamente"
            disabled={true}
          />
          {errors.email && (
            <p className="text-red-600 text-sm mt-1">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6">
        <Button
          bsStyle={Button.DEFAULT}
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          bsStyle={Button.PRIMARY}
          type="submit"
          disabled={loading || loadingUser}
          iconName={loading ? undefined : "rioglyph-check"}
        >
          {loading ? (
            <div className="flex items-center">
              <Spinner />
              Adicionando...
            </div>
          ) : (
            'Adicionar'
          )}
        </Button>
      </div>
    </form>
  );
};

export default NotificationUserForm;

